import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/hooks/queries';
import { captureError } from '@/utils/errorHandler';

const AdminAppContext = createContext(null);

export function AdminAppProvider({ children, initialApp, authFetch, showToast, navigate }) {
  const queryClient = useQueryClient();
  const [app, setApp] = useState(initialApp);
  const [loading, setLoading] = useState(false);
  
  // Track preferred currency separately to reduce dependencies in callbacks
  const preferredCurrency = app?.preferredCurrency || 'usd';

  // Update state when initial values change (e.g., after initial load)
  useEffect(() => {
    if (initialApp) {
      setApp(initialApp);
      // Update currency when app changes to ensure it matches preferred currency
      setQuoteCurrency(initialApp.preferredCurrency || 'usd');
    }
  }, [initialApp]);
  
  // Modal states
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showGoLiveModal, setShowGoLiveModal] = useState(false);
  const [showStatusChangeModal, setShowStatusChangeModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  
  // Form states
  const [quoteAmount, setQuoteAmount] = useState('');
  // Initialize currency from initialApp if available, otherwise default to 'usd'
  const [quoteCurrency, setQuoteCurrency] = useState(initialApp?.preferredCurrency || 'usd');
  const [monthlyPrice, setMonthlyPrice] = useState('5.00');
  const [quoteNote, setQuoteNote] = useState('');
  const [depositPercentage, setDepositPercentage] = useState(20);
  const [productionUrl, setProductionUrl] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [addingNote, setAddingNote] = useState(false);

  const loadApp = async (appId) => {
    // Invalidate and refetch app query
    await queryClient.invalidateQueries({ queryKey: queryKeys.admin.app(appId) });
  };

  const handleSendQuote = () => {
    setQuoteAmount(app?.quotedAmount || '');
    setQuoteCurrency(app?.preferredCurrency || 'usd');
    setMonthlyPrice(app?.monthlyMaintenanceFee || '5.00');
    setQuoteNote('');
    setDepositPercentage(20);
    setShowQuoteModal(true);
  };

  const submitQuote = async (appId) => {
    if (!quoteAmount) {
      showToast('Please enter the total amount', 'error');
      return;
    }

    if (!monthlyPrice) {
      showToast('Please enter the monthly price', 'error');
      return;
    }

    setSubmitting(true);
    try {
      // Backend handles currency logic: if user has preferredCurrency, it uses that (locked).
      // If user doesn't have preferredCurrency, it uses the currency from the request.
      // Always send quoteCurrency first - admin's selection when user has no preference.
      const currency = quoteCurrency || app?.preferredCurrency || 'usd';
      
      const response = await authFetch('/api/admin/send-quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appId,
          totalAmount: parseFloat(quoteAmount),
          currency,
          monthlyPrice: parseFloat(monthlyPrice),
          note: quoteNote.trim() || null,
          depositPercentage: Number(depositPercentage),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send quote');
      }

      showToast('✅ Quote sent successfully!', 'success');
      setShowQuoteModal(false);
      
      // Invalidate queries to refresh data
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin.app(appId) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin.quotesNeeded });
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin.stats });
    } catch (error) {
      captureError(error, {
        action: 'submitQuote',
        component: 'AdminAppContext',
        extra: { appId, quoteAmount, quoteCurrency, monthlyPrice },
      });
      console.error('Error sending quote:', error);
      showToast(error.message || 'Failed to send quote', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddNote = async (appId, content) => {
    if (!content.trim()) {
      showToast('Please enter a note', 'error');
      return;
    }

    setAddingNote(true);
    try {
      const response = await authFetch('/api/build-requests/add-note', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appId,
          content: content.trim(),
          source: 'admin',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add note');
      }

      showToast('✅ Note added and email sent!', 'success');
      
      // Invalidate app query to refresh notes
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin.app(appId) });
    } catch (error) {
      captureError(error, {
        action: 'handleAddNote',
        component: 'AdminAppContext',
        extra: { appId },
      });
      console.error('Error adding note:', error);
      showToast(error.message || 'Failed to add note', 'error');
    } finally {
      setAddingNote(false);
    }
  };

  const handleStatusChangeClick = (status) => {
    if (status === app?.status) return;
    setNewStatus(status);
    setShowStatusChangeModal(true);
  };

  const submitStatusChange = async (appId) => {
    if (!newStatus || newStatus === app?.status) {
      setShowStatusChangeModal(false);
      return;
    }

    try {
      setSubmitting(true);
      const response = await authFetch('/api/apps/update', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: appId,
          status: newStatus,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update status');
      }

      showToast(`✅ Status changed to ${newStatus}`, 'success');
      setShowStatusChangeModal(false);
      setNewStatus('');
      await loadApp(appId);
    } catch (error) {
      captureError(error, {
        action: 'submitStatusChange',
        component: 'AdminAppContext',
        extra: { appId, newStatus },
      });
      console.error('Error updating status:', error);
      showToast(error.message || 'Failed to update status', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const closeQuoteModal = () => {
    setShowQuoteModal(false);
    setQuoteAmount('');
    setQuoteCurrency(app?.preferredCurrency || 'usd');
    setMonthlyPrice('5.00');
    setQuoteNote('');
    setDepositPercentage(20);
  };

  const submitGoLive = async (appId) => {
    try {
      setSubmitting(true);
      const response = await authFetch('/api/admin/go-live', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appId,
          productionUrl: productionUrl.trim(),
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to go live');
      }

      showToast('🚀 App is now live!', 'success');
      setShowGoLiveModal(false);
      await loadApp(appId);
    } catch (error) {
      captureError(error, {
        action: 'submitGoLive',
        component: 'AdminAppContext',
        extra: { appId, productionUrl },
      });
      console.error('Error going live:', error);
      showToast('Failed to go live', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteApp = () => {
    setDeleteConfirmText('');
    setShowDeleteModal(true);
  };

  const submitDeleteApp = async (appId) => {
    if (deleteConfirmText !== app?.name) {
      showToast('Please type the app name to confirm deletion', 'error');
      return;
    }

    try {
      setSubmitting(true);
      const response = await authFetch('/api/admin/delete-app', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ appId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete app');
      }

      showToast('🗑️ App deleted successfully', 'success');
      setShowDeleteModal(false);
      setDeleteConfirmText('');
      
      // Navigate back to apps list
      navigate('/admin-portal/apps');
    } catch (error) {
      captureError(error, {
        action: 'submitDeleteApp',
        component: 'AdminAppContext',
        extra: { appId },
      });
      console.error('Error deleting app:', error);
      showToast(error.message || 'Failed to delete app', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteConfirmText('');
  };

  // Computed values
  const hasQuote = app?.quotedAt != null;
  const adminNotes = app?.adminNotes || [];

  const value = {
    // Data
    app,
    loading,
    hasQuote,
    adminNotes,
    
    // Actions
    loadApp,
    setApp,
    
    // Quote
    showQuoteModal,
    setShowQuoteModal,
    quoteAmount,
    setQuoteAmount,
    quoteCurrency,
    setQuoteCurrency,
    monthlyPrice,
    setMonthlyPrice,
    quoteNote,
    setQuoteNote,
    depositPercentage,
    setDepositPercentage,
    handleSendQuote,
    submitQuote,
    closeQuoteModal,
    
    // Status
    showStatusChangeModal,
    setShowStatusChangeModal,
    newStatus,
    setNewStatus,
    handleStatusChangeClick,
    submitStatusChange,
    
    // Transfer
    showTransferModal,
    setShowTransferModal,
    
    // Go Live
    showGoLiveModal,
    setShowGoLiveModal,
    productionUrl,
    setProductionUrl,
    submitGoLive,
    
    // Delete
    showDeleteModal,
    setShowDeleteModal,
    deleteConfirmText,
    setDeleteConfirmText,
    handleDeleteApp,
    submitDeleteApp,
    closeDeleteModal,
    
    // Notes
    addingNote,
    handleAddNote,
    
    // General
    submitting,
    setSubmitting,
    authFetch,
    showToast,
    navigate,
  };

  return (
    <AdminAppContext.Provider value={value}>
      {children}
    </AdminAppContext.Provider>
  );
}

export function useAdminApp() {
  const context = useContext(AdminAppContext);
  if (!context) {
    throw new Error('useAdminApp must be used within AdminAppProvider');
  }
  return context;
}
